<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useGlobal, useImageDownloader, useMap, usePois } from '@/composables';

const externalContainer = ref<HTMLDivElement | null>(null);
const { handleLoadAsset, isFinished } = useImageDownloader();
const {
  isRgb,
  zoomIn,
  zoomOut,
  isHighQuality,
  IMAGE_SIZE,
  init,
  isDragDisabled,
  mapTransitionState,
  apply,
} = useMap();
const { isPoiFocused, isDragging } = useGlobal();
const { selectedPoi } = usePois();

const urls: { [key: string]: URL } = {};

const getMapFull = (type: 'rgb' | 'bw') => {
  const key = `map-${type}-hq`;
  if (!(key in urls)) {
    urls[key] = new URL(`/src/assets/images/Maps/half/map-${type}-hq.avif?url`, import.meta.url);
  }

  return urls[key].href;
};

const getMapFullQuarter = (type: 'rgb' | 'bw') => {
  const key = `map-${type}-quarter`;
  if (!(key in urls)) {
    urls[key] = new URL(`/src/assets/images/Maps/map-${type}-quarter.avif?url`, import.meta.url);
  }

  return urls[key].href;
};

onMounted(() => {
  init(externalContainer.value);
});

const handleWheel = useDebounceFn((event: WheelEvent) => {
  if (!isPoiFocused.value) {
    const sensitivity = 1;
    if (event.deltaY <= sensitivity) {
      apply(() => zoomIn(2));
    }
    if (event.deltaY > sensitivity) {
      apply(() => zoomOut(2));
    }
  }
}, 200);

const cursor = computed(() => {
  if (isDragDisabled.value) return 'cursor-default';
  return isDragging.value ? 'cursor-grabbing' : 'cursor-grab';
});

const handleMapClick = () => {
  if (isPoiFocused.value) {
    apply(
      () => {
        selectedPoi.value = null;
        isPoiFocused.value = false;
      },
      {
        restoreState: true,
      },
    );
  }
};

const style = computed(() => ({
  transform: `translate3d(${mapTransitionState.value[0]}px, ${mapTransitionState.value[1]}px, 0) scale(${mapTransitionState.value[2]})`,
}));
</script>

<template>
  <div class="relative h-dvh w-dvw touch-none overflow-hidden">
    <div
      ref="externalContainer"
      :style="style"
      class="fixed"
      :class="cursor"
      @wheel.prevent="handleWheel"
    >
      <div
        class="fixed"
        :style="`height: ${IMAGE_SIZE.height}px; width: ${IMAGE_SIZE.width}px`"
        @click="handleMapClick"
      >
        <div v-if="!isFinished" data-map-preload>
          <img
            class="pointer-events-none absolute z-map-bw-quarter size-full select-none"
            :src="getMapFullQuarter('bw')"
            alt=""
            @load="handleLoadAsset"
          />
          <img
            class="pointer-events-none absolute z-map-bw-quarter size-full select-none"
            :src="getMapFull('bw')"
            alt=""
            @load="handleLoadAsset"
          />
          <img
            class="pointer-events-none absolute z-map-rgb-quarter size-full select-none"
            :src="getMapFullQuarter('rgb')"
            alt=""
            @load="handleLoadAsset"
          />
          <img
            class="pointer-events-none absolute z-map-rgb-hq size-full select-none"
            :src="getMapFull('rgb')"
            alt=""
            @load="handleLoadAsset"
          />
        </div>
        <div data-map-bw class="absolute size-full">
          <img
            class="pointer-events-none absolute z-map-bw-quarter size-full select-none"
            :src="getMapFullQuarter('bw')"
            alt=""
            v-once
          />
          <div v-show="isHighQuality" class="absolute z-map-bw-hq size-full">
            <img
              class="pointer-events-none absolute z-map-bw-hq size-full select-none"
              :src="getMapFull('bw')"
              alt=""
              v-once
            />
          </div>
        </div>
        <div v-show="isRgb" data-map-rgb class="absolute size-full">
          <img
            class="pointer-events-none absolute z-map-rgb-quarter size-full select-none"
            :src="getMapFullQuarter('rgb')"
            alt=""
            v-once
          />
          <div v-show="isHighQuality" class="absolute z-map-rgb-hq size-full">
            <img
              class="pointer-events-none absolute z-map-rgb-hq size-full select-none"
              :src="getMapFull('rgb')"
              alt=""
              v-once
            />
          </div>
        </div>
        <slot />
      </div>
    </div>
    <slot name="viewport" />
  </div>
</template>
